import {
  useLocation,
  useParams,
  useNavigate,
} from "react-router-dom";

import {
  useEffect,
  useState,
  useMemo,
} from "react";

import {
  doc,
  getDoc,
} from "firebase/firestore";

import { db } from "../firebase";

import "./BetScores.css";

/* =========================================================
   TIMEZONE
========================================================= */

const TIMEZONE = "Africa/Dar_es_Salaam";

/* =========================================================
   QUANTITY SETTINGS
========================================================= */

const QUANTITY_SETTINGS = {
  MAX_QUANTITY: 1900000000,
  START_PERCENT: 55,
  DYNAMIC_WINDOW_MINUTES: 24 * 60,
  MIN_START_QUANTITY: 10000000,
  QUANTITY_STEP: 1000,
};

/* =========================================================
   BET SCORES
========================================================= */

export default function BetScores() {
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();

  /* =======================================================
     STATE
  ======================================================= */

  const [match, setMatch] = useState(state || null);
  const [scores, setScores] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("ant");

  const [matchLoading, setMatchLoading] = useState(!state);
  const [scoresLoading, setScoresLoading] = useState(true);

  const [currentTime, setCurrentTime] = useState(
    () => new Date()
  );

  /* =======================================================
     LIVE CLOCK
  ======================================================= */

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  /* =======================================================
     LOAD MATCH FROM FIREBASE
  ======================================================= */

  useEffect(() => {
    if (match) {
      setMatchLoading(false);
      return;
    }

    if (!id) {
      setMatchLoading(false);
      return;
    }

    let cancelled = false;

    const fetchMatch = async () => {
      try {
        setMatchLoading(true);

        const matchRef = doc(db, "matches", id);
        const snap = await getDoc(matchRef);

        if (cancelled) {
          return;
        }

        if (snap.exists()) {
          setMatch({
            id: snap.id,
            ...snap.data(),
          });
        } else {
          console.error("Match haijapatikana");
          setMatch(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Match load error:", error);
          setMatch(null);
        }
      } finally {
        if (!cancelled) {
          setMatchLoading(false);
        }
      }
    };

    fetchMatch();

    return () => {
      cancelled = true;
    };
  }, [id, match]);

  /* =======================================================
     PARSE MATCH DATE
  ======================================================= */

  const parseMatchDate = (date, time) => {
    if (!date) {
      return null;
    }

    /*
      Firestore Timestamp
    */

    if (typeof date?.toDate === "function") {
      return date.toDate();
    }

    /*
      JavaScript Date
    */

    if (date instanceof Date) {
      return date;
    }

    const dateString = String(date).trim();

    const timeString = time
      ? String(time).trim()
      : "00:00";

    /*
      ISO datetime
    */

    if (
      dateString.includes("T") ||
      dateString.includes(" ")
    ) {
      const normalized = dateString.replace(" ", "T");

      const hasTimezone =
        /Z$|[+-]\d{2}:\d{2}$/.test(normalized);

      const dateTimeString = hasTimezone
        ? normalized
        : `${normalized}+03:00`;

      const existing = new Date(dateTimeString);

      if (!Number.isNaN(existing.getTime())) {
        return existing;
      }
    }

    /*
      YYYY-MM-DD + HH:mm
    */

    const eatDateTime = new Date(
      `${dateString}T${timeString}:00+03:00`
    );

    if (!Number.isNaN(eatDateTime.getTime())) {
      return eatDateTime;
    }

    return null;
  };

  /* =======================================================
     MATCH START TIME
  ======================================================= */

  const matchStartTime = useMemo(() => {
    if (!match) {
      return null;
    }

    return parseMatchDate(
      match.date,
      match.time
    );
  }, [match]);

  /* =======================================================
     MINUTES UNTIL START
  ======================================================= */

  const getMinutesUntilStart = () => {
    if (!matchStartTime) {
      return null;
    }

    const difference =
      matchStartTime.getTime() -
      currentTime.getTime();

    return difference / (1000 * 60);
  };

  /* =======================================================
     GET BASE QUANTITY
  ======================================================= */

  const getBaseQuantity = (item, index) => {
    if (
      item &&
      item.quantity !== undefined &&
      item.quantity !== null &&
      item.quantity !== ""
    ) {
      const value = Number(item.quantity);

      if (
        Number.isFinite(value) &&
        value > 0
      ) {
        return Math.min(
          value,
          QUANTITY_SETTINGS.MAX_QUANTITY
        );
      }
    }

    const fallbackQuantities = [
      13256000,
      27381000,
      47381000,
      68243000,
      91756000,
      126547000,
      174263000,
      238451000,
      326784000,
      417526000,
      538214000,
      647381000,
      782645000,
      894217000,
      1026380000,
      1184720000,
      1325680000,
      1473820000,
      1584263000,
      1718526000,
      1826374000,
      1897426000,
    ];

    return Math.min(
      fallbackQuantities[
        index % fallbackQuantities.length
      ],
      QUANTITY_SETTINGS.MAX_QUANTITY
    );
  };

  /* =======================================================
     CREATE DIFFERENT START QUANTITY
  ======================================================= */

  const getStartingQuantity = (
    baseQuantity,
    index
  ) => {
    const startPercent =
      QUANTITY_SETTINGS.START_PERCENT;

    let starting =
      baseQuantity *
      (startPercent / 100);

    const variations = [
      0.73,
      0.81,
      0.87,
      0.92,
      0.76,
      0.84,
      0.89,
      0.95,
      0.79,
      0.86,
      0.91,
      0.97,
    ];

    const variation =
      variations[index % variations.length];

    starting *= variation;

    starting = Math.max(
      QUANTITY_SETTINGS.MIN_START_QUANTITY,
      starting
    );

    starting = Math.min(
      starting,
      baseQuantity,
      QUANTITY_SETTINGS.MAX_QUANTITY
    );

    return starting;
  };

  /* =======================================================
     ROUND QUANTITY
  ======================================================= */

  const roundQuantity = (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return 0;
    }

    const step =
      QUANTITY_SETTINGS.QUANTITY_STEP;

    return (
      Math.round(numericValue / step) *
      step
    );
  };

  /* =======================================================
     ADD NATURAL DIGITS
  ======================================================= */

  const addNaturalDigits = (
    value,
    index
  ) => {
    const digitPatterns = [
      132,
      347,
      581,
      726,
      843,
      263,
      419,
      657,
      782,
      915,
      536,
      684,
    ];

    const pattern =
      digitPatterns[
        index % digitPatterns.length
      ];

    const extra = pattern * 1000;

    return Number(value) + extra;
  };

  /* =======================================================
     DYNAMIC QUANTITY
  ======================================================= */

  const calculateDynamicQuantity = (
    baseQuantity,
    index
  ) => {
    const safeBase = Math.min(
      Number(baseQuantity),
      QUANTITY_SETTINGS.MAX_QUANTITY
    );

    if (
      !Number.isFinite(safeBase) ||
      safeBase <= 0
    ) {
      return 0;
    }

    const minutesUntilStart =
      getMinutesUntilStart();

    /*
      Muda wa mechi haujulikani
    */

    if (minutesUntilStart === null) {
      const natural = addNaturalDigits(
        safeBase,
        index
      );

      return roundQuantity(
        Math.min(
          natural,
          safeBase,
          QUANTITY_SETTINGS.MAX_QUANTITY
        )
      );
    }

    const startingQuantity =
      getStartingQuantity(
        safeBase,
        index
      );

    /*
      Mechi imeanza
    */

    if (minutesUntilStart <= 0) {
      return roundQuantity(safeBase);
    }

    /*
      Bado zaidi ya masaa 24
    */

    const dynamicWindow =
      QUANTITY_SETTINGS.DYNAMIC_WINDOW_MINUTES;

    if (minutesUntilStart >= dynamicWindow) {
      const naturalStart = addNaturalDigits(
        startingQuantity,
        index
      );

      return roundQuantity(
        Math.min(
          naturalStart,
          safeBase,
          QUANTITY_SETTINGS.MAX_QUANTITY
        )
      );
    }

    /*
      Progress ndani ya masaa 24
    */

    const progress =
      1 -
      minutesUntilStart / dynamicWindow;

    const safeProgress = Math.max(
      0,
      Math.min(1, progress)
    );

    const rawQuantity =
      startingQuantity +
      (safeBase - startingQuantity) *
        safeProgress;

    const withDigits = addNaturalDigits(
      rawQuantity,
      index
    );

    const finalQuantity = Math.min(
      withDigits,
      safeBase,
      QUANTITY_SETTINGS.MAX_QUANTITY
    );

    return roundQuantity(finalQuantity);
  };

  /* =======================================================
     READ CORRECT SCORES
  ======================================================= */

  useEffect(() => {
    if (!match) {
      setScores([]);
      setScoresLoading(false);
      return;
    }

    setScoresLoading(true);

    try {
      if (
        Array.isArray(match.correctScores) &&
        match.correctScores.length > 0
      ) {
        const scoreData = match.correctScores
          .filter(
            (item) =>
              item &&
              item.score !== undefined &&
              item.score !== null &&
              String(item.score).trim() !== ""
          )
          .map((item, index) => ({
            id:
              item.id ||
              `${String(item.score)}-${index}`,

            score: String(item.score).trim(),

            odds: item.odds ?? null,

            quantity: item.quantity ?? null,
          }));

        setScores(scoreData);
      } else {
        setScores([]);
      }
    } catch (error) {
      console.error(
        "Scores parse error:",
        error
      );

      setScores([]);
    }

    setScoresLoading(false);
  }, [match]);

  /* =======================================================
     GET DISPLAYED ODDS
  ======================================================= */

  const getDisplayedOdds = (
    odds,
    mode = tab
  ) => {
    if (
      odds === undefined ||
      odds === null ||
      odds === ""
    ) {
      return 0;
    }

    const numericValue = Number(
      String(odds)
        .replace("%", "")
        .trim()
    );

    if (!Number.isFinite(numericValue)) {
      return 0;
    }

    const multiplier =
      mode === "correct" ? 10 : 1;

    const convertedValue =
      numericValue * multiplier;

    return Number.isInteger(convertedValue)
      ? convertedValue
      : Number(
          convertedValue.toFixed(2)
        );
  };

  /* =======================================================
     FORMAT ODDS
  ======================================================= */

  const formatOdds = (
    odds,
    multiplier = 1
  ) => {
    if (
      odds === undefined ||
      odds === null ||
      odds === ""
    ) {
      return "-";
    }

    const value = String(odds).trim();

    if (!value) {
      return "-";
    }

    const numericValue = Number(
      value.replace("%", "").trim()
    );

    if (!Number.isFinite(numericValue)) {
      return "-";
    }

    const convertedValue =
      numericValue * multiplier;

    const finalValue = Number.isInteger(
      convertedValue
    )
      ? convertedValue
      : Number(
          convertedValue.toFixed(2)
        );

    return `${finalValue}%`;
  };

  /* =======================================================
     GET CURRENT QUANTITY
  ======================================================= */

  const getQuantity = (
    item,
    index
  ) => {
    const baseQuantity = getBaseQuantity(
      item,
      index
    );

    return calculateDynamicQuantity(
      baseQuantity,
      index
    );
  };

  /* =======================================================
     FORMAT QUANTITY
  ======================================================= */

  const formatQuantity = (quantity) => {
    const value = Number(quantity);

    if (!Number.isFinite(value) || value < 0) {
      return "0K";
    }

    // Display only: 14,534,345 -> 14,534K.
    // The original numeric quantity remains unchanged for Firebase/BetPlace.
    const thousands = Math.round(value / 1000);

    return `${thousands.toLocaleString("en-GB")}K`;
  };

  /* =======================================================
     OPEN MARKET CHART
  ======================================================= */

  const openMarketChart = () => {
    const matchId =
      match?.id || id;

    if (!matchId) {
      console.error("Match ID haipo");
      return;
    }

    navigate(
      `/market-chart/${matchId}`,
      {
        state: {
          match,
        },
      }
    );
  };

  /* =======================================================
     HANDLE BET
  ======================================================= */

  const handleClick = (
    item,
    index
  ) => {
    if (!item) {
      return;
    }

    const quantity = getQuantity(
      item,
      index
    );

    /*
      Hii ndiyo odds halisi itakayotumwa
      kwenda BetPlace.jsx.
    */
    const displayedOdds =
      getDisplayedOdds(
        item.odds,
        tab
      );

    const selectedItem = {
      ...item,
      odds: displayedOdds,
      quantity,
      mode: tab,
    };

    setSelected(selectedItem);

    const selection = {
      score: item.score,
      odds: displayedOdds,
      quantity,
      mode: tab,
    };

    setTimeout(() => {
      navigate(
        "/betplace",
        {
          state: {
            match,
            selection,
          },
        }
      );
    }, 200);
  };

  /* =======================================================
     CHANGE TAB
  ======================================================= */

  const changeTab = (newTab) => {
    setTab(newTab);
    setSelected(null);
  };

  /* =======================================================
     MATCH LOADING
  ======================================================= */

  if (matchLoading) {
    return (
      <div className="bet-page">
        <div className="loading">
          Loading match...
        </div>
      </div>
    );
  }

  /* =======================================================
     MATCH NOT FOUND
  ======================================================= */

  if (!match) {
    return (
      <div className="bet-page">
        <div className="loading">
          Match not found
        </div>
      </div>
    );
  }

  /* =======================================================
     TEAM NAMES
  ======================================================= */

  const teamA =
    match.A ||
    match.teamA ||
    "Team A";

  const teamB =
    match.B ||
    match.teamB ||
    "Team B";

  /* =======================================================
     LEAGUE
  ======================================================= */

  const league =
    match.league ||
    "League";

  /* =======================================================
     MINUTES DISPLAY
  ======================================================= */

  const minutesUntilStart =
    getMinutesUntilStart();

  /* =======================================================
     MATCH STATUS TEXT
  ======================================================= */

  const getMatchStatusText = () => {
    if (minutesUntilStart === null) {
      return "";
    }

    if (minutesUntilStart <= 0) {
      return "Started";
    }

    if (minutesUntilStart < 60) {
      return `Starts in ${Math.ceil(
        minutesUntilStart
      )} min`;
    }

    if (minutesUntilStart < 24 * 60) {
      const hours = Math.floor(
        minutesUntilStart / 60
      );

      const minutes = Math.ceil(
        minutesUntilStart % 60
      );

      return `Starts in ${hours}h ${minutes}m`;
    }

    const days = Math.floor(
      minutesUntilStart / (24 * 60)
    );

    const hours = Math.floor(
      (minutesUntilStart % (24 * 60)) / 60
    );

    return `Starts in ${days}d ${hours}h`;
  };

  /* =======================================================
     UI
  ======================================================= */

  return (
    <div className="bet-page">
      {/* MATCH HEADER */}

      <div className="bet-header">
        <div className="teams">
          <div className="team">
            <div
              className="team-ball"
              aria-label="Football"
            >
              ⚽
            </div>

            <span>
              {teamA}
            </span>
          </div>

          <span className="vs">
            VS
          </span>

          <div className="team">
            <div
              className="team-ball"
              aria-label="Football"
            >
              ⚽
            </div>

            <span>
              {teamB}
            </span>
          </div>
        </div>

        <p className="league">
          {league}
        </p>

        {getMatchStatusText() && (
          <div className="match-countdown">
            {getMatchStatusText()}
          </div>
        )}
      </div>

      {/* TABS */}

      <div className="tabs">
        <div
          className={
            tab === "ant"
              ? "tab active"
              : "tab"
          }
          role="button"
          tabIndex={0}
          onClick={() => changeTab("ant")}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" ||
              event.key === " "
            ) {
              event.preventDefault();
              changeTab("ant");
            }
          }}
        >
          SecureScore
        </div>

        <div
          className={
            tab === "correct"
              ? "tab active"
              : "tab"
          }
          role="button"
          tabIndex={0}
          onClick={() => changeTab("correct")}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" ||
              event.key === " "
            ) {
              event.preventDefault();
              changeTab("correct");
            }
          }}
        >
          UnsecureScore
        </div>
      </div>

      {/* SCORE LOADING */}

      {scoresLoading && (
        <div className="loading">
          Loading scores...
        </div>
      )}

      {/* SCORE TABLE */}

      {!scoresLoading &&
        scores.length > 0 && (
          <div className="score-table">
            <div className="score-table-header">
              <div>
                Item
              </div>

              <div>
                Profit
              </div>

              <div className="quantity-title">
                Quantity
              </div>

              <div className="chart-header">
                <button
                  type="button"
                  className="header-chart-btn"
                  onClick={openMarketChart}
                  title="Market Chart"
                  aria-label="Open Market Chart"
                >
                  📈 🔺
                </button>
              </div>
            </div>

            {scores.map((item, index) => {
              const quantity = getQuantity(
                item,
                index
              );

              const displayedOdds =
                getDisplayedOdds(
                  item.odds,
                  tab
                );

              const active =
                selected?.score === item.score;

              return (
                <div
                  key={
                    item.id ||
                    `${item.score}-${index}`
                  }
                  className={
                    active
                      ? "score-table-row selected-row"
                      : "score-table-row"
                  }
                >
                  <div className="table-score">
                    {item.score}
                  </div>

                  <div className="table-profit">
                    {formatOdds(
                      displayedOdds,
                      1
                    )}
                  </div>

                  <div className="table-quantity">
                    {formatQuantity(quantity)}
                  </div>

                  <button
                    type="button"
                    className="table-bet-btn"
                    onClick={() =>
                      handleClick(
                        item,
                        index
                      )
                    }
                    aria-label={`Bet ${item.score}`}
                  >
                    BET
                  </button>
                </div>
              );
            })}
          </div>
        )}

      {/* NO SCORES */}

      {!scoresLoading &&
        scores.length === 0 && (
          <div className="loading">
            Match hii haina scores na odds.
          </div>
        )}

      {/* SELECTED BET */}

      {selected && (
        <div className="bet-box">
          Selected:{" "}

          <strong>
            {selected.score}
          </strong>

          {" — "}

          {formatOdds(
            selected.odds,
            1
          )}

          {" — "}

          {formatQuantity(
            selected.quantity
          )}
        </div>
      )}
    </div>
  );
}